/**
 * シャンチー (中国象棋) 盤コンポーネント。 9 列 × 10 行 grid + 駒は交点上の円形 SVG。
 *
 * - 駒は **常に正立** (= 将棋と違って 180° 回転しない)。 owner は色 (紅 vs 黒) で区別。
 * - viewer 視点で自陣が下に来るよう、 Side='Second' のときは盤を flip 表示。
 * - 河界 (rank 4 と 5 の間) と九宮 (各 3x3 の対角線) を背景に描画。
 * - last-move リング (.last-from / .last-to) は共通スタイル。
 * - 王手中の自将に強調 (.in-check)。
 */

import { useState } from 'react';
import {
  legalTargetsFor,
  pieceGlyph,
  type Side,
  XIANGQI_FILES,
  XIANGQI_RANKS,
  type XiangqiMove,
  type XiangqiPiece,
  type XiangqiState,
} from '../../games/xiangqi';

interface Props {
  state: XiangqiState;
  legal?: XiangqiMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: XiangqiMove) => void;
  mini?: boolean;
  lastMove?: XiangqiMove;
  /** Replay / Archive / チュートリアル等の静的ビューでは「相手の手番」 表示を出さない。 */
  staticView?: boolean;
  /** チュートリアル: 強調する交点 [file, rank] の配列。 */
  tutorialHighlight?: [number, number][];
}

export function XiangqiBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  tutorialHighlight,
}: Props) {
  const isMyTurn =
    myTurnAllowed !== undefined ? myTurnAllowed : yourSide != null && yourSide === state.side;
  const showOppHint = !mini && !staticView && !isMyTurn;
  const [selected, setSelected] = useState<[number, number] | null>(null);

  // 自分側が下になるよう並べ替える。
  // 内部は rank=0 が紅最下段 (画面下) として固定。 Second 視点では rank/file 反転。
  const flip = yourSide === 'Second';
  // 画面表示順 = 上から下の rank 列 (画面上が rank=RANKS-1)
  const displayRanks: number[] = flip
    ? Array.from({ length: XIANGQI_RANKS }, (_, i) => i)
    : Array.from({ length: XIANGQI_RANKS }, (_, i) => XIANGQI_RANKS - 1 - i);
  const displayFiles: number[] = flip
    ? Array.from({ length: XIANGQI_FILES }, (_, i) => XIANGQI_FILES - 1 - i)
    : Array.from({ length: XIANGQI_FILES }, (_, i) => i);

  const targets = selected ? legalTargetsFor(legal ?? [], selected) : [];

  const handleCellClick = (file: number, rank: number) => {
    if (mini || !isMyTurn) return;
    const cell = state.board[rank]?.[file] ?? null;
    if (!selected) {
      if (cell && cell.owner === state.side) {
        setSelected([file, rank]);
      }
      return;
    }
    // ターゲット候補
    const cand = targets.find((m) => m.to[0] === file && m.to[1] === rank);
    if (cand) {
      onMove?.(cand);
      setSelected(null);
      return;
    }
    // 別の自駒選択
    if (cell && cell.owner === state.side) {
      setSelected([file, rank]);
      return;
    }
    setSelected(null);
  };

  const lastFrom = lastMove?.from ?? null;
  const lastTo = lastMove?.to ?? null;

  return (
    <div className={`xiangqi-frame ${mini ? 'mini' : ''} ${showOppHint ? 'not-my-turn' : ''}`}>
      <div className="xiangqi-board">
        {/* 河 / 九宮の背景レイヤ。 mini モードでは省略してもよいがコスト極小なので常に描く */}
        <svg
          className="xiangqi-bg"
          viewBox={`0 0 ${XIANGQI_FILES * 100} ${XIANGQI_RANKS * 100}`}
          preserveAspectRatio="none"
          aria-hidden
        >
          {/* 縦線 */}
          {Array.from({ length: XIANGQI_FILES }, (_, f) => (
            <line
              key={`v-${f}`}
              x1={f * 100 + 50}
              y1={50}
              x2={f * 100 + 50}
              y2={XIANGQI_RANKS * 100 - 50}
              stroke="currentColor"
              strokeWidth={2}
              opacity={0.55}
            />
          ))}
          {/* 横線 (1..RANKS-1 の 9 本) */}
          {Array.from({ length: XIANGQI_RANKS }, (_, r) => (
            <line
              key={`h-${r}`}
              x1={50}
              y1={r * 100 + 50}
              x2={XIANGQI_FILES * 100 - 50}
              y2={r * 100 + 50}
              stroke="currentColor"
              strokeWidth={2}
              opacity={0.55}
            />
          ))}
          {/* 河界 (rank 4 と 5 の間に "楚河 / 漢界" 風の帯) */}
          <rect
            x={50}
            y={4 * 100 + 50}
            width={(XIANGQI_FILES - 1) * 100}
            height={100}
            fill="var(--accent-soft, #e8d9b8)"
            opacity={0.45}
          />
          <text
            x={(XIANGQI_FILES * 100) * 0.28}
            y={5 * 100 + 8}
            fill="var(--xq-red, #b13a3a)"
            fontSize={56}
            fontWeight={700}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            楚 河
          </text>
          <text
            x={(XIANGQI_FILES * 100) * 0.72}
            y={5 * 100 + 8}
            fill="var(--xq-red, #b13a3a)"
            fontSize={56}
            fontWeight={700}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            漢 界
          </text>
          {/* 九宮: 紅 (rank 0..=2, file 3..=5) の対角線 ×2 */}
          <line x1={3 * 100 + 50} y1={50} x2={5 * 100 + 50} y2={2 * 100 + 50} stroke="currentColor" strokeWidth={2} opacity={0.55} />
          <line x1={5 * 100 + 50} y1={50} x2={3 * 100 + 50} y2={2 * 100 + 50} stroke="currentColor" strokeWidth={2} opacity={0.55} />
          {/* 九宮: 黒 (rank 7..=9, file 3..=5) */}
          <line x1={3 * 100 + 50} y1={7 * 100 + 50} x2={5 * 100 + 50} y2={9 * 100 + 50} stroke="currentColor" strokeWidth={2} opacity={0.55} />
          <line x1={5 * 100 + 50} y1={7 * 100 + 50} x2={3 * 100 + 50} y2={9 * 100 + 50} stroke="currentColor" strokeWidth={2} opacity={0.55} />
        </svg>
        {displayRanks.flatMap((rank) =>
          displayFiles.map((file) => {
            const cell = state.board[rank]?.[file] ?? null;
            const isSelected = selected != null && selected[0] === file && selected[1] === rank;
            const isTarget = targets.some((m) => m.to[0] === file && m.to[1] === rank);
            const isCap = isTarget && cell != null;
            const isLast =
              !mini &&
              ((lastFrom != null && lastFrom[0] === file && lastFrom[1] === rank) ||
                (lastTo != null && lastTo[0] === file && lastTo[1] === rank));
            const isTutorialHint =
              tutorialHighlight?.some(([hf, hr]) => hf === file && hr === rank) ?? false;
            return (
              <button
                key={`${file}-${rank}`}
                className={`xq-cell ${isSelected ? 'sel' : ''} ${
                  isTarget ? (isCap ? 'cap' : 'cand') : ''
                } ${isLast ? 'last-move' : ''} ${isTutorialHint ? 'tutorial-hint' : ''}`}
                disabled={mini}
                onClick={() => handleCellClick(file, rank)}
                aria-label={`file${file}-rank${rank}`}
              >
                {cell && <XiangqiPieceVisual piece={cell} />}
              </button>
            );
          }),
        )}
      </div>
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}

function XiangqiPieceVisual({ piece }: { piece: XiangqiPiece }) {
  const colorClass = piece.owner === 'First' ? 'red' : 'black';
  return (
    <span className={`xiangqi-piece ${colorClass}`}>
      <span className="xiangqi-piece-disc">{pieceGlyph(piece)}</span>
    </span>
  );
}
